import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  Badge,
  Button,
  Form,
  InputGroup,
  Pagination,
  Table,
} from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { ordersApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import OrderDetailsPanel from "../components/OrderDetailsPanel";
import { hasShippingDetails } from "../utils/orderAddress";
import {
  buildPageItems,
  filterAndSortOrders,
  ORDER_PAGE_SIZES,
  ORDER_SORT_OPTIONS,
  paginateItems,
} from "../utils/adminOrders";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../constants/seo";
import { TableSkeleton } from "../components/Skeleton";
import "./dashboard/ManagePages.css";

const ORDER_STATUSES = [
  "pending",
  "confirmed",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
];

const STATUS_VARIANT = {
  pending: "secondary",
  confirmed: "info",
  processing: "primary",
  shipped: "warning",
  delivered: "success",
  cancelled: "danger",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "all",
  emirate: "all",
  dateFrom: "",
  dateTo: "",
  sortBy: "newest",
};

const MyOrders = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();
  const inDashboard = location.pathname.startsWith("/dashboard");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    ordersApi
      .mine()
      .then((res) => setOrders(res.data.data.orders || []))
      .catch((err) => toast.error(getErrorMessage(err, "Failed to load orders")))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const filteredOrders = useMemo(
    () =>
      filterAndSortOrders(orders, {
        ...filters,
        emirate: "all",
      }),
    [orders, filters]
  );

  const pagination = useMemo(
    () => paginateItems(filteredOrders, page, pageSize),
    [filteredOrders, page, pageSize]
  );

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const hasActiveFilters =
    filters.search.trim() ||
    filters.status !== "all" ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.sortBy !== "newest";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setExpandedId(null);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setExpandedId(null);
  };

  const pageItems = buildPageItems(pagination.page, pagination.totalPages);

  if (authLoading) return <TableSkeleton rows={6} cols={7} />;
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: inDashboard ? "/dashboard/orders" : "/orders" }}
        replace
      />
    );
  }

  const tableSection = (
    <>
      {!loading && orders.length > 0 && (
        <div className="manage-toolbar">
          <InputGroup className="manage-search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search order #, address, phone…"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>

          <Form.Select
            className="manage-filter"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <option value="all">All statuses</option>
            {ORDER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
          />
          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
          />

          <Form.Select
            className="manage-filter"
            value={filters.sortBy}
            onChange={(e) => updateFilter("sortBy", e.target.value)}
          >
            {ORDER_SORT_OPTIONS.filter((o) =>
              ["newest", "oldest", "total_desc", "total_asc", "order_number"].includes(
                o.value
              )
            ).map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter manage-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {ORDER_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="manage-clear-btn"
              onClick={clearFilters}
            >
              <FaTimes /> Clear
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={6} cols={7} />
      ) : orders.length === 0 ? (
        <div className="manage-empty">
          <p>You have no orders yet.</p>
          <Link to="/products">Continue shopping</Link>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="manage-empty">
          <p>No orders match your search or filters.</p>
          <Button variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div className="manage-card table-responsive">
            <Table hover className="manage-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Delivery</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((order) => {
                  const shipping = order.shippingAddress || {};
                  const isExpanded = expandedId === order._id;

                  return (
                    <React.Fragment key={order._id}>
                      <tr>
                        <td>
                          <Link to={`/order-tracking?order=${order.orderNumber}`}>
                            <code>{order.orderNumber}</code>
                          </Link>
                        </td>
                        <td>
                          {order.createdAt
                            ? new Date(order.createdAt).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          {hasShippingDetails(shipping) ? (
                            <>
                              <div>{shipping.emirate || shipping.city || "—"}</div>
                              <div className="manage-muted">{shipping.line1}</div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{order.items?.length || 0}</td>
                        <td>AED {Number(order.total).toFixed(2)}</td>
                        <td>
                          <Badge bg={STATUS_VARIANT[order.status] || "dark"}>
                            {order.status}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <Button
                            variant="link"
                            size="sm"
                            className="manage-expand-btn"
                            onClick={() =>
                              setExpandedId(isExpanded ? null : order._id)
                            }
                          >
                            {isExpanded ? (
                              <>
                                <FaChevronUp /> Hide
                              </>
                            ) : (
                              <>
                                <FaChevronDown /> Details
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="manage-order-details-row">
                          <td colSpan={7}>
                            <OrderDetailsPanel order={order} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="manage-pagination-bar">
            <p className="manage-pagination-meta">
              Showing {pagination.showingFrom}–{pagination.showingTo} of{" "}
              {pagination.total} orders
            </p>
            {pagination.totalPages > 1 && (
              <Pagination className="manage-pagination mb-0">
                <Pagination.Prev
                  disabled={pagination.page <= 1}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    setExpandedId(null);
                  }}
                />
                {pageItems.map((item, index) =>
                  item === "…" ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === pagination.page}
                      onClick={() => {
                        setPage(item);
                        setExpandedId(null);
                      }}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => {
                    setPage((p) => Math.min(pagination.totalPages, p + 1));
                    setExpandedId(null);
                  }}
                />
              </Pagination>
            )}
          </div>
        </>
      )}
    </>
  );

  if (inDashboard) {
    return (
      <div className="manage-page">
        <header className="manage-head">
          <div>
            <h1>My Orders</h1>
            <p>Review your past and current orders with delivery details.</p>
          </div>
          <span className="manage-count">
            {filteredOrders.length === orders.length
              ? `${orders.length} total`
              : `${filteredOrders.length} of ${orders.length}`}
          </span>
        </header>
        {tableSection}
      </div>
    );
  }

  return (
    <main className="manage-page" style={{ padding: "40px 7% 80px" }}>
      <Seo {...PAGE_SEO.orders} />
      <header className="manage-head">
        <div>
          <h1>My Orders</h1>
          <p>Review your past and current orders with delivery details.</p>
        </div>
        <span className="manage-count">
          {filteredOrders.length === orders.length
            ? `${orders.length} total`
            : `${filteredOrders.length} of ${orders.length}`}
        </span>
      </header>
      {tableSection}
    </main>
  );
};

export default MyOrders;
